import { inject, Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class FormService {
  private msg = inject(NzMessageService);
  constructor() {}

  parentComponent: any;

  public validate(
    form: any,
    options: any = {}
  ): { isValid: boolean; invalidControls: any[] } {
    if (!form) {
      return { isValid: false, invalidControls: [] };
    }
    

    const invalidControls: any[] = [];
    // Recursive function to validate nested controls
    const validateControls = (controls: any, parentKey: string = '') => {
      Object.keys(controls).forEach(key => {
        const control = controls[key];
        const controlKey = parentKey ? `${parentKey}.${key}` : key;

        // Skip if invalid key exists
        if (
          invalidControls.find(
            o => o.key.toLowerCase() == controlKey.toLowerCase()
          )
        ) {
          return;
        }

        if (control.controls) {
          // If the control is a FormGroup or FormArray, recursively validate its controls
          validateControls(control.controls, controlKey);
        } else {
          control.markAsDirty();
          control.updateValueAndValidity(options);
          if (control.errors) {
            let invalidKey = controlKey;
            if (invalidKey.endsWith('Id')) {
              invalidKey = invalidKey.substring(0, invalidKey.length - 2);
            }
            const capitalizedKey =
              this.capitalizeFirstLetterOfEachWord(invalidKey);
            invalidControls.push({
              key: capitalizedKey,
              errors: control.errors
            });
            if (this.parentComponent && this.parentComponent.focusElement) {
              this.parentComponent.focusElement(invalidKey);
            } else {
              if (this.focusInvalidControl()) return;
            }
          }
        }
      });
    };

    // Start validation from the root form controls
    validateControls(form.controls);

    const isValid = form.valid;
    return { isValid, invalidControls };
  }

  public async validateAsync(form: any): Promise<boolean> {
    if (!form) {
      return false;
    }

    // Non-recursive function to validate controls
    const validateControlsAsync = async (control: any, controlKey: string) => {
      if (!control) return;

      // Trigger validation
      control.markAsDirty();
      control.updateValueAndValidity();

      // If the control has nested controls, validate them as well
      if (control.controls) {
        Object.keys(control.controls).forEach(async key => {
          await validateControlsAsync(
            control.controls[key],
            `${controlKey}.${key}`
          );
        });
      }
    };

    // Start validation from the root form controls
    Object.keys(form.controls).forEach(async key => {
      await validateControlsAsync(form.controls[key], key);
    });

    // Wait for the validation result to complete
    if (form.status === 'PENDING') {
      try {
        await new Promise(resolve => setTimeout(resolve, 1));
        await firstValueFrom(form.statusChanges);
      } catch (error) {
        console.error('Validation error:', error);
        // Handle the error or rethrow if necessary
      }
    }

    return form.valid;
  }

  public getInvalidControls(form: any) {
    const invalidControls: any[] = [];

    // Non-recursive function to validate controls
    const collectInvalidControls = (control: any, controlKey: string) => {
      if (!control) return;

      // Skip if invalid key exists
      if (
        invalidControls.find(
          o => o.key.toLowerCase() === controlKey.toLowerCase()
        )
      )
        return;

      if (control.errors) {
        let invalidKey = controlKey;
        if (invalidKey.endsWith('Id')) {
          invalidKey = invalidKey.substring(0, invalidKey.length - 2);
        }
        const capitalizedKey = this.capitalizeFirstLetterOfEachWord(invalidKey);
        invalidControls.push({
          key: capitalizedKey,
          errors: control.errors
        });

        if (this.parentComponent && this.parentComponent.focusElement) {
          this.parentComponent.focusElement(invalidKey);
        } else {
          this.focusInvalidControl();
        }
      }

      // If the control has nested controls, validate them as well
      if (control.controls) {
        Object.keys(control.controls).forEach(key => {
          collectInvalidControls(control.controls[key], `${controlKey}.${key}`);
        });
      }
    };

    // Collect invalid controls from the root form controls
    Object.keys(form.controls).forEach(key => {
      collectInvalidControls(form.controls[key], key);
    });

    return invalidControls;
  }

  public enable(form: any) {
    if (!form) {
      return;
    }
    Object.keys(form.controls).forEach(key => {
      form.controls[key].enable();
    });
  }

  public resetErrors(form: any) {
    if (!form) {
      return;
    }
    Object.keys(form.controls).forEach(key => {
      form.controls[key].setErrors(null);
    });
  }

  public disable(form: any) {
    if (!form) {
      return;
    }
    Object.keys(form.controls).forEach(key => {
      const control = form.controls[key];
      if (control instanceof FormArray) {
        control.controls.forEach(fg => {
          this.disable(fg);
        });
      } else if (control instanceof FormGroup) {
        this.disable(control);
      } else {
        form.controls[key].disable();
      }
    });
  }
  public updateFormState(form: any, editing: boolean) {
    if (editing) this.enable(form);
    else this.disable(form);
  }

  public focusInvalidControl(): boolean {
    const invalidControl = document
      .querySelector('form')
      ?.querySelector('.ng-invalid');

    if (invalidControl) {
      if (invalidControl.tagName === 'INPUT') {
        (<any>invalidControl).focus();
        return true;
      }
      const input: any = invalidControl.querySelector('input');
      if (input) {
        input.focus();
        return true;
      }
    }
    return false;
  }

  public hasError(form: any, field: string, type: string) {
    if (form) {
      var control = form.form.get(field);
      if (!control || !control.errors) return false;

      return form && control?.dirty && control.errors[type];
    }
    return false;
  }

  public showValidationMsg(invalidControls: any[]) {
    const errorMessages = invalidControls.map(control => {
      const errorList = Object.keys(control.errors).map(key => {
        const fieldLabel = this.transformControlNameToLabeling(control.key);
        
        // If the error has a custom message, use it.
        const msg = control.errors[key]?.message;
        if (msg) return msg;

        switch (key) {
          case 'required':
            return `${fieldLabel} is required.`;
          case 'maxlength':
            return `${fieldLabel} has a maximum length of ${control.errors[key].requiredLength} characters.`;
          default:
            return `${fieldLabel} has an invalid value.`;
        }
      });
      return errorList.join('<br>');
    });

    const detailedMessage = errorMessages.join('<br>'); // Join error messages with <br> for new lines in HTML
    this.msg.error(detailedMessage, { nzDuration: 6000, nzPauseOnHover: true });
  }

  transformControlNameToLabeling(value: string): string {
    if (!value) return value;

    // Example: "ItemDetails.0.Classification" => "Classification Row 1 in Details"
    const parts = value.split('.');
    if (
      parts.length > 2 &&
      (parts[0] === 'ItemDetails' || parts[0] === 'Details')
    ) {
      const rowIndex = parseInt(parts[1], 10) + 1; // Convert zero-based index to one-based
      return `${parts[2]} of row ${rowIndex} in Details`;
    }
    // Convert PascalCase to space-separated and capitalize the first character
    const spaced = value.replace(/(?<!^)(?=[A-Z])/g, ' ');

    // Capitalize the first character of the resulting string
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  capitalizeFirstLetterOfEachWord(str: string): string {
    //\b(\w)(\w*) = regex pattern to find words
    return str.replace(/\b\w/g, char => char.toUpperCase());
  }
}
